import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, setPendingMFA } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
        if (isRegister) {
          const response = await authService.register({
            email,
            password,
            full_name: fullName,
          });
          login(response, rememberMe);
          navigate('/dashboard');
        } else {
          const response = await authService.login({ email, password });
  
          if (response.requires_mfa) {
            setPendingMFA(email);
            navigate('/mfa');
          } else {
            login(response, rememberMe);
            navigate('/dashboard');
          }
        }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error en la autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        .login-input::placeholder {
          color: rgba(255, 255, 255, 0.35) !important;
        }
        .login-input:focus {
          border-color: var(--accent) !important;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15) !important;
          background: rgba(255, 255, 255, 0.08) !important;
        }
        .login-input:hover {
          border-color: rgba(255, 255, 255, 0.3) !important;
          background: rgba(255, 255, 255, 0.06) !important;
        }
        .login-btn:hover {
          filter: brightness(1.1);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
        }
        .login-btn:active {
          transform: translateY(0);
        }
      `}</style>
      <div style={styles.card}>
        <div style={styles.brand}>
          <img src="/logo.png" alt="Unio AI Logo" style={styles.logoImage} />
        </div>

        <h1 style={styles.title}>
          {isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'}
        </h1>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {isRegister && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nombre Completo</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="login-input"
                style={styles.input}
                placeholder="Tu nombre"
              />
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input"
              style={styles.input}
              placeholder="demo@jjagency.com"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="login-input"
              style={styles.input}
              placeholder="••••••"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: 'var(--accent)' }}
            />
            <label htmlFor="rememberMe" style={{ fontSize: '0.875rem', color: 'var(--text-sidebar)', cursor: 'pointer', userSelect: 'none' }}>
              Mantener mi sesión iniciada
            </label>
          </div>

          <button type="submit" disabled={loading} className="login-btn" style={styles.button}>
            {loading ? 'Procesando...' : isRegister ? 'Registrarse' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-page)',
    fontFamily: 'var(--font-body)',
    padding: '1rem',
  },
  card: {
    background: 'var(--bg-sidebar)',
    padding: '2.5rem',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '420px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  brand: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
  },
  logoImage: {
    height: '140px',
    width: 'auto',
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25))',
  },
  title: {
    textAlign: 'center',
    color: '#ffffff',
    marginBottom: '1.5rem',
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.125rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--text-sidebar)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  input: {
    padding: '0.85rem 1rem',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
    fontFamily: 'var(--font-body)',
  },
  button: {
    padding: '0.9rem',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'background 0.2s, transform 0.1s, box-shadow 0.2s',
    fontFamily: 'var(--font-body)',
  },
  error: {
    background: 'rgba(220, 38, 38, 0.12)',
    color: '#f87171',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    marginBottom: '1rem',
    border: '1px solid rgba(220, 38, 38, 0.25)',
  },
};

export default Login;
